const ATTRIBUTION_STORAGE_KEY = 'motito_attribution_v1';

const ATTRIBUTION_KEYS = [
  'referral_code',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'src',
  'partner',
  'partner_code',
  'click_id',
  'gclid',
  'fbclid',
  'ttclid',
] as const;

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];

export type StoredAttribution = Partial<Record<AttributionKey, string>>;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function getStorage(storage?: StorageLike): StorageLike | null {
  if (storage) {
    return storage;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function normalizeValue(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function sanitizeStoredAttribution(value: unknown): StoredAttribution {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const sanitized: StoredAttribution = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const normalized = normalizeValue((value as Record<string, unknown>)[key]);
    if (normalized) {
      sanitized[key] = normalized;
    }
  });

  return sanitized;
}

export function extractAttributionParams(searchParams: URLSearchParams): StoredAttribution {
  const extracted: StoredAttribution = {};

  const referralCode =
    normalizeValue(searchParams.get('referral_code'))
    || normalizeValue(searchParams.get('ref'))
    || normalizeValue(searchParams.get('program_invitation_code'));
  if (referralCode) {
    extracted.referral_code = referralCode;
  }

  ATTRIBUTION_KEYS.forEach((key) => {
    if (key === 'referral_code') {
      return;
    }

    const value = normalizeValue(searchParams.get(key));
    if (value) {
      extracted[key] = value;
    }
  });

  return extracted;
}

export function readStoredAttribution(storage?: StorageLike): StoredAttribution {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return {};
  }

  try {
    const raw = resolvedStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    return sanitizeStoredAttribution(JSON.parse(raw));
  } catch {
    return {};
  }
}

export function persistAttributionFromSearch(
  searchParams: URLSearchParams,
  storage?: StorageLike
): StoredAttribution {
  const resolvedStorage = getStorage(storage);
  const existing = readStoredAttribution(resolvedStorage ?? undefined);
  const incoming = extractAttributionParams(searchParams);

  if (!resolvedStorage || Object.keys(incoming).length === 0) {
    return existing;
  }

  const merged: StoredAttribution = { ...existing };
  Object.entries(incoming).forEach(([key, value]) => {
    const typedKey = key as AttributionKey;
    if (!merged[typedKey] && value) {
      merged[typedKey] = value;
    }
  });

  try {
    resolvedStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    return existing;
  }

  return merged;
}

export function getStoredReferralCode(storage?: StorageLike): string | undefined {
  return normalizeValue(readStoredAttribution(storage).referral_code ?? null);
}

export function clearStoredAttribution(storage?: StorageLike): void {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Ignore storage failures; attribution persistence is best-effort.
  }
}
